import React, { useEffect, useRef, useState } from "react";
import Topbar from "./Topbar";
// import logo from "./images/logo.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IoSearch, IoMenu, IoClose } from "react-icons/io5";
import { LuUser } from "react-icons/lu";
import { CiHeart } from "react-icons/ci";
import { BsHandbag } from "react-icons/bs";
import { navLinks } from "../data/links";
import { useSelector } from "react-redux";
import gsap from "gsap";

const Navbar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(null);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { quantity } = useSelector((state) => state.cart);

  const navbarRef = useRef(null);
  const logoRef = useRef(null);
  const linksRef = useRef(null);
  const iconsRef = useRef(null);
  const mobileBtnRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // PAGE LOAD ANIMATION
  useEffect(() => {
    const tl = gsap.timeline();

    // Navbar slide down
    tl.from(navbarRef.current, {
      y: -100,
      opacity: 0,
      duration: 0.5,
      ease: "power2.out",
    });

    // Logo, links, icons, mobile btn fade in sequentially
    tl.from(
      [
        logoRef.current,
        linksRef.current,
        iconsRef.current,
        mobileBtnRef.current,
      ],
      {
        opacity: 0,
        y: -20,
        stagger: 0.2,
        duration: 0.5,
        ease: "power2.out",
      },
      "-=0.3", // overlap slightly with navbar slide
    );
  }, []);

  // MOBILE MENU ANIMATION
  useEffect(() => {
    if (isOpen) {
      gsap.fromTo(
        mobileMenuRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" },
      );
    }
  }, [isOpen]);
  useEffect(() => {
    // Function to detect clicks outside mobile menu
    const handleClickOutside = (event) => {
      if (
        isOpen && // only when menu is open
        mobileMenuRef.current && // make sure ref exists
        !mobileMenuRef.current.contains(event.target) && // click is outside menu
        !mobileBtnRef.current.contains(event.target) // and not on the toggle button
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);
  const icons = [
    { id: 1, title: "profile", icon: <LuUser />, link: "/user" },
    { id: 2, title: "Cart", icon: <BsHandbag />, link: "/cart" },
  ];
  const isLinkActive = (linkPath) => {
    const [path, query] = linkPath.split("?");

    // Check pathname
    if (location.pathname !== path) return false;

    // If link has no query → only match pathname
    if (!query) return true;

    // If link has query → match full search string
    return location.search === `?${query}`;
  };
  const handleSearch = () => {
    if (query.trim() !== "") {
      // Redirect to product page with query as URL param
      navigate(`/fragrance?search=${encodeURIComponent(query)}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };
  return (
    <nav
      ref={navbarRef}
      className="relative py-4 z-50 shadow-[0_4px_10px_rgba(0,0,0,0.15)]"
    >
      <div className="px-[1rem] sm:px-[4rem] mx-auto flex justify-between items-center">
        {/* Logo */}
        <div
          ref={logoRef}
          className="logo h-[70px] w-[200px] bg-black px-4 rounded-[50px] flex-shrink-0 cursor-pointer"
          onClick={() => navigate("/")}
        >
          <img
            src="/images/logo.png"
            alt="Logo"
            className="h-full w-full sm:w-[130px] md:w-auto object-contain"
          />
        </div>

        {/* Desktop Links */}
        <div
          ref={linksRef}
          className="hidden lg:flex items-center py-[1rem] px-[1rem] rounded-[30px] gap-6 text-[16px] font-Outfit"
        >
          {navLinks.map((link, idx) => {
            const isActive = isLinkActive(link.link);

            return (
              <div key={idx} className="relative">
                <Link
                  to={link.link}
                  className="relative inline-block group text-gray-800 font-normal font-Outfit transition-all duration-300 ease-in-out"
                  onMouseEnter={() => setOpenDrawer(idx)}
                  onMouseLeave={() => setOpenDrawer(null)}
                >
                  {link.tilte}
                  <span
                    className={`absolute bottom-0 left-0 h-[2px] bg-gray-800 transition-all duration-300 ease-in-out ${
                      isActive ? "w-full" : "w-0"
                    } group-hover:w-full`}
                  ></span>
                </Link>

                {/* Drawer / Sublinks */}
                {link.sublink && (
                  <div
                    className={`fixed top-[45px] left-0 right-0 bg-white text-black shadow-lg overflow-hidden transition-all duration-500 rounded-[20px] ${
                      openDrawer === idx
                        ? "max-h-[500px] opacity-100"
                        : "max-h-0 opacity-0 pointer-events-none"
                    }`}
                    onMouseEnter={() => setOpenDrawer(idx)}
                    onMouseLeave={() => setOpenDrawer(null)}
                  >
                    <div className="max-w-[1200px] mx-auto flex justify-between p-4">
                      {link.sublink.map((data, index) => (
                        <div
                          key={index}
                          className="flex-1 p-4 border-b last:border-b-0"
                        >
                          <h2
                            className={`font-bold text-[20px] mb-2 ${
                              !data.itms ? "cursor-pointer" : ""
                            }`}
                            onClick={() => navigate(`${data.link}`)}
                          >
                            {data.title}
                          </h2>
                          <ul className="flex flex-col gap-1">
                            {data.itms?.map((item) => (
                              <li key={item.id}>
                                <Link
                                  to={item.link}
                                  className="hover:text-yellow-500 transition-colors text-[16px]"
                                >
                                  {item.title}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop Icons */}
        <div
          ref={iconsRef}
          className="hidden lg:flex items-center gap-4 text-[20px] text-gray-800"
        >
          <div className="w-[150px] flex justify-between items-center overflow-hidden rounded-[40px] p-[0.2rem] border border-text-gray-800">
            <input
              type="text"
              name="search"
              id="search"
              placeholder="Search.."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="h-full w-[80%] rounded-[40px] text-[16px] px-[0.5rem] bg-transparent focus:none outline-none"
            />
            <div
              onClick={handleSearch}
              className="cursor-pointer h-[1.5rem] w-[1.5rem] flex justify-center items-center text-black rounded-full gradient"
            >
              <IoSearch />
            </div>
          </div>

          {icons.map((item, idx) => (
            <div key={idx} className="relative">
              <button
                onClick={() => navigate(item.link)}
                className="hover:text-[#f0dc82] text-[20px] transition-colors duration-300 ease-in-out"
              >
                {item.icon}
              </button>
              {item.title === "Cart" && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">
                  {quantity !== 0 ? quantity : 0}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <div
          ref={mobileBtnRef}
          className="lg:hidden flex justify-center text-gray-800 items-center gap-[1rem]"
        >
          {icons.map((item, idx) => (
            <div key={idx} className="relative">
              <button
                onClick={() => navigate(item.link)}
                className="hover:text-gray-500 text-[20px] transition-colors duration-300 ease-in-out"
              >
                {item.icon}
              </button>
              {item.title === "Cart" && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">
                  {quantity !== 0 ? quantity : 0}
                </div>
              )}
            </div>
          ))}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-gray-800 text-3xl hover:text-gray-500 transition-colors duration-300 ease-in-out"
          >
            {isOpen ? <IoClose /> : <IoMenu />}
          </button>
        </div>
      </div>
      <div className="px-[1rem] flex lg:hidden mt-[1rem]">
        <div className="w-full flex justify-between items-center overflow-hidden rounded-[40px] p-[0.3rem] border border-gray-500">
          <input
            type="text"
            name="search"
            id="search"
            placeholder="Search.."
            className="h-full w-[80%] rounded-[40px] text-[16px] text-gray-800 px-[0.5rem] bg-transparent focus:none outline-none"
          />
          <div className="cursor-pointer h-[1.5rem] w-[1.5rem] flex justify-center items-center text-black rounded-full gradient">
            <IoSearch />
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      {isOpen && (
        <div
          ref={mobileMenuRef}
          className="absolute top-full left-0 w-full bg-black px-6 py-4 flex flex-col gap-4 text-white font-Outfit z-50"
        >
          {navLinks.map((link, idx) => (
            <Link
              key={idx}
              to={link.link}
              className="hover:text-[#f0dc82] transition-colors duration-300 ease-in-out"
              onClick={() => setIsOpen(false)}
            >
              {link.tilte}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
